import config, { SOURCE_EXTRACT_SCOPE } from "../config";
import { exchangeOAuthCodeOnce } from "./oauthExchangeDedup";
import { fetchJsonDesktopSafe } from "./desktopFetch";

// ===== Integration Authentication =====

// Integrations that rely on service-account delegation instead of OAuth
const SERVICE_ACCOUNT_INTEGRATIONS = ["gmail", "google_calendar"];

/**
 * Internal helper – generates or re-uses a service-account and returns the
 * backend payload (contains credential_id, instructions, etc.).
 */
const generateServiceAccount = async (integrationType) => {
  const endpoint = integrationType.toLowerCase();
  const resp = await fetch(
    `${config.BACKEND_URL}/externalauth/${endpoint}/service_account`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ integration_name: integrationType }),
    }
  );
  const data = await resp.json();
  if (!resp.ok || data.status === "error") {
    const err = new Error(data.message || "Service-account generation failed");
    err.payload = data;
    throw err;
  }
  return data; // ← has credential_id, instructions, message, …
};

export const authenticateIntegration = async (
  integrationType,
  authType = null
) => {
  console.log(`Redirecting to ${integrationType} OAuth`);

  try {
    // Normalize the integration type for the API endpoint
    const endpoint = integrationType.toLowerCase();
    const lower = integrationType.toLowerCase();

    // 1. Service-account based (now only for non-individual Gmail)
    if (
      SERVICE_ACCOUNT_INTEGRATIONS.includes(lower) &&
      authType !== SOURCE_EXTRACT_SCOPE.INDIVIDUAL
    ) {
      return await generateServiceAccount(integrationType);
    }

    // Fetch the auth URL from the backend
    const authUrlResponse = await fetch(
      `${config.BACKEND_URL}/externalauth/${endpoint}/auth_url`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    const data = await authUrlResponse.json().catch(() => ({}));
    console.log(`Auth URL for ${integrationType}:`, data.auth_url);

    // A failed auth_url used to fall through to `window.location.href =
    // undefined`, which navigates to a relative "undefined" path: the SPA
    // rewrite serves index.html, no route matches, and the user silently lands
    // back on the connectors list with no error at all. Fail loudly instead --
    // the caller renders `error.message`.
    if (!authUrlResponse.ok || (endpoint !== "datagen" && !data.auth_url)) {
      throw new Error(
        data.error ||
          data.message ||
          `Could not start ${integrationType} authorization` +
            (authUrlResponse.ok ? "" : ` (HTTP ${authUrlResponse.status})`)
      );
    }

    // Special handling for Datagen: perform the callback directly and return the result
    if (endpoint === "datagen") {
      const response = await fetch(
        `${config.BACKEND_URL}/externalauth/datagen/callback`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Datagen authentication failed");
      }
      const result = await response.json();
      return result; // allow caller to set credential state without redirect
    }

    // Redirect the user to the auth URL for standard OAuth providers
    window.location.href = data.auth_url;
  } catch (error) {
    console.error(`Error authenticating with ${integrationType}:`, error);
    throw error;
  }
  return null;
};

/**
 * POST authorization result to /externalauth/{provider}/callback.
 *
 * @example
 * await postExternalAuthCallback("zoom", "abc123");
 * // => { credential_id: "cred_xyz", message: "..." }
 */
export const postExternalAuthCallback = async (provider, body) => {
  const endpoint = String(provider || "").toLowerCase();
  return fetchJsonDesktopSafe(
    `${config.BACKEND_URL}/externalauth/${endpoint}/callback`,
    { method: "POST", body }
  );
};

/**
 * Handles the OAuth redirect for any integration type.
 * @param {string} integrationType - The type of integration (from INTEGRATION_TYPES)
 * @returns {Promise<object|null>} The credential data if successful, null otherwise
 */
export const handleAuthRedirect = async (integrationType) => {
  const urlParams = new URLSearchParams(window.location.search);

  // Get parameters for both OAuth code flow and Microsoft admin consent flow
  const code = urlParams.get("code");
  const tenant = urlParams.get("tenant");
  const adminConsent = urlParams.get("admin_consent");
  const state = urlParams.get("state");

  // Return null if neither flow's parameters are present
  if (!code && !(tenant && adminConsent)) return null;

  const endpoint = integrationType.toLowerCase();

  try {
    if (code) {
      return await exchangeOAuthCodeOnce(endpoint, code, async (authCode) => {
        const body = { code: authCode };
        // MCP providers (Granola, Attio) need the full state for PKCE pending lookup.
        if (state) {
          body.state = state;
        }
        return postExternalAuthCallback(endpoint, body);
      });
    }

    const consentKey = `admin_consent:${tenant}:${adminConsent}`;
    return await exchangeOAuthCodeOnce(endpoint, consentKey, async () =>
      postExternalAuthCallback(endpoint, {
        tenant,
        admin_consent: adminConsent,
      })
    );
  } catch (error) {
    console.error(`Error in handleAuthRedirect for ${integrationType}:`, error);
    return null;
  }
};

/**
 * Fetches the available authentication options for Gmail.
 */
export const getGmailAuthOptions = async () => {
  try {
    const response = await fetch(
      `${config.BACKEND_URL}/externalauth/gmail/get_auth_options`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch Gmail auth options");
    }
    const data = await response.json();
    return data.options || [];
  } catch (error) {
    console.error("Error fetching Gmail auth options:", error);
    return []; // Return empty array on error
  }
};

export const getGoogleCalendarAuthOptions = async () => {
  try {
    const response = await fetch(
      `${config.BACKEND_URL}/externalauth/google_calendar/get_auth_options`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch Google Calendar auth options");
    }
    const data = await response.json();
    return data.options || [];
  } catch (error) {
    console.error("Error fetching Google Calendar auth options:", error);
    return [];
  }
};

/**
 * Helper function to detect the integration type from the URL during redirect
 * @returns {string|null} The detected integration type or null
 */
export const detectIntegrationTypeFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const state = urlParams.get("state");

  if (state) {
    try {
      const stateData = JSON.parse(decodeURIComponent(state));
      return stateData.integration_type;
    } catch {
      return null;
    }
  }

  // Fallback detection based on other URL parameters
  // This can be expanded based on how different providers handle redirects
  if (urlParams.has("code")) {
    // Try to identify from sessionStorage
    return sessionStorage.getItem("sourceIntegrationName");
  }

  return null;
};
